import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";
import { IMarketPricing } from "./useMarketPricing";
import { IUser } from "./useUsers";

interface IBadge {
  _id: string;
  name: string;
  description: string;
  color: string;
  type: "FIRST_DROP" | "DROP_COUNT" | "DROP_VALUE" | "ITEM_TYPE";
  minDrops?: number;
  minValue?: number;
  itemType?: IMarketPricing;
  rewardDroppies?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface IUserRedeemedBadge {
  _id: string;
  badge: IBadge;
  user: IUser;
  redeemedAt: Date;
}

interface IStore {
  /** State */
  redeemedUsersByBadgeId: IUserRedeemedBadge[];
  userProfileBadges: IBadge[];
  oneUserProfileBadge: IBadge | null;
  createdUserProfileBadge: string | null;
  updatedUserProfileBadge: string | null;
  deletedUserProfileBadge: string | null;
  /** Callbacks */
  getRedeemedUsersByBadgeId: (badgeId: string) => void;
  deleteRedeemedUsersByBadgeId: (badgeId: string) => void;
  getUserProfileBadges: () => void;
  getOneUserProfileBadge: (id: string) => void;
  createUserProfileBadge: (badge: IBadge) => void;
  updateUserProfileBadge: (id: string, badge: IBadge) => void;
  deleteUserProfileBadge: (id: string) => void;
  clearCreatedUserProfileBadge: () => void;
  clearUpdatedUserProfileBadge: () => void;
  clearDeletedUserProfileBadge: () => void;
}

export const useUserProfileBadges = create<IStore>()((set, get) => ({
  /** State */
  redeemedUsersByBadgeId: [],
  userProfileBadges: [],
  oneUserProfileBadge: null,
  createdUserProfileBadge: null,
  updatedUserProfileBadge: null,
  deletedUserProfileBadge: null,
  /** Callbacks */
  getRedeemedUsersByBadgeId: async (badgeId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `/user-badges/badges-redeemed/${badgeId}`
      );
      if (response?.data) {
        set({ redeemedUsersByBadgeId: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching redeemed users by badge id", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteRedeemedUsersByBadgeId: async (badgeId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`/user-badges/redeemed/${badgeId}`);
      if (response?.data) {
        set({
          redeemedUsersByBadgeId: get().redeemedUsersByBadgeId.filter(
            (user) => user._id !== response.data?._id
          ),
        });
      }
    } catch (error) {
      LOG.error("Error on deleting redeemed users by badge id", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getUserProfileBadges: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("/user-badges");
      if (response?.data) {
        set({ userProfileBadges: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching badges", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneUserProfileBadge: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`/user-badges/${id}`);
      if (response?.data) {
        set({ oneUserProfileBadge: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one badge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  createUserProfileBadge: async (badge: IBadge) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("/user-badges", badge);
      if (response?.data) {
        set({ createdUserProfileBadge: response.data?._id });
      }
    } catch (error) {
      LOG.error("Error on creating badge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateUserProfileBadge: async (id: string, badge: IBadge) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`/user-badges/${id}`, badge);
      if (response?.data) {
        set({ updatedUserProfileBadge: response.data?._id });
      }
    } catch (error) {
      LOG.error("Error on updating badge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteUserProfileBadge: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`/user-badges/${id}`);
      if (response?.data) {
        set((state) => ({
          userProfileBadges: state.userProfileBadges.filter(
            (badge) => badge._id !== response.data?._id
          ),
          deletedUserProfileBadge: response.data?._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting badge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearCreatedUserProfileBadge: () => {
    set({ createdUserProfileBadge: null });
  },
  clearUpdatedUserProfileBadge: () => {
    set({ updatedUserProfileBadge: null });
  },
  clearDeletedUserProfileBadge: () => {
    set({ deletedUserProfileBadge: null });
  },
}));
